import { nanoid } from 'nanoid';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../../components/common/button/button';
import WidgetComponent from '../../components/widget-component';
import ProgressBar from '../../components/progress-bar';
import { UnlockIcon, LockIcon } from '../../assets/icons';
import { OrganisationContext } from '../../context/organisationContext';
import { getErrorDescription } from '../../helpers/utils';
import useFormStore from '../../store/formStore';
import {
  getFormConfigs,
  getFormDetails,
  getFormPanelWidgets,
  getFormPanels,
  getSession,
  getSessionDetails,
  updateSession,
  closeSession,
  reopenSession,
} from '../../store/features/quoteSlice';
import { addToast } from '../../store/features/toastSlice';
import { MainWrapper } from '../../styles/pages/main.styled';

const Main = ({ form, sessionId: sessionIdProp }) => {
  // Fallback: Extract sessionId from URL if not passed as prop
  const getSessionIdFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('session_id') || null;
  };
  const sessionIdFromURL = getSessionIdFromURL();
  const sessionId = sessionIdProp || sessionIdFromURL;

  const dispatch = useDispatch();
  const { id, form: formParam } = useParams();
  const form_id = form || formParam || id;
  const [formDetails, setFormDetails] = useState({});
  const [formPanels, setFormPanels] = useState([]);
  const [formConfigs, setFormConfigs] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState({});
  const [loading, setLoading] = useState(false);
  const [components, setComponensts] = useState([]);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Track original session data to detect changes
  const [originalSessionValues, setOriginalSessionValues] = useState({});

  // Track if session is closed (locked for editing)
  const [isSessionClosed, setIsSessionClosed] = useState(false);

  // Zustand store for form values
  const { values, currentPanelIndex, setValue, setCurrentPanelIndex, resetForm } = useFormStore();

  const { api_key } = formDetails || {};
  const { panel_color, transparency } = selectedPanel || { panel_color: '#FFFFFF' };

  // Calculate maximum panel width from all panels to keep widget size consistent
  const maxPanelWidth = React.useMemo(() => {
    const baseWidth = formDetails?.attributes?.[0]?.panel_width || 0;

    if (!formPanels || formPanels.length === 0) {
      return baseWidth || null;
    }

    // Get panel widths - check if panels have their own width attributes
    const panelWidths = formPanels.map(panel => {
      // Check if panel has its own width attribute
      const panelWidth = panel?.attributes?.[0]?.panel_width || panel?.panel_width || baseWidth;
      return panelWidth || 0;
    });

    // Return the maximum width found, ensuring it's at least the base width
    return Math.max(...panelWidths, baseWidth) || null;
  }, [formPanels, formDetails]);

  const resetState = () => {
    resetForm();
  };

  const fetchFormDetails = form_id => {
    if (!form_id) {
      return;
    }
    dispatch(getFormDetails({ form_id: form_id }))
      .then(data => {
        setFormDetails(data);
      })
      .catch(error => {
        console.error('Error fetching form details:', error);
        dispatch(addToast({ error: true, text: 'Failed to fetch form details', id: nanoid() }));
      });
  };

  const fetchFormPanels = form_id => {
    if (!api_key || !form_id) {
      return;
    }
    dispatch(getFormPanels({ api_key, form_id }))
      .then(data => {
        setFormPanels(data);
        if (data && data.length > 0) {
          setSelectedPanel(data[currentPanelIndex] || data[0]);
          if (currentPanelIndex >= data.length) {
            setCurrentPanelIndex(0);
          }
        }
      })
      .catch(error => {
        console.error('Error fetching form panels:', error);
        setFormPanels([]);
      });
  };

  const goToNextPanel = () => {
    // Validate required fields on current panel before advancing
    const currentComponents = components.flat();
    const missingRequired = currentComponents.filter(c => {
      if (!c.is_required || !c.lexicon) return false;
      const val = values[c.lexicon];
      return val === null || val === undefined || val === '';
    });

    if (missingRequired.length > 0) {
      const errors = {};
      missingRequired.forEach(c => {
        errors[c.lexicon] = true;
        const fieldName = (c.label || c.name || 'Unknown field').split('\n')[0].trim().slice(0, 60);
        dispatch(addToast({ error: true, title: 'Required field', text: fieldName, id: nanoid() }));
      });
      setFieldErrors(errors);
      return;
    }

    if (currentPanelIndex < formPanels.length - 1) {
      setFieldErrors({});
      const nextIndex = currentPanelIndex + 1;
      setCurrentPanelIndex(nextIndex);
      setSelectedPanel(formPanels[nextIndex]);
    }
  };

  const goToPreviousPanel = () => {
    if (currentPanelIndex > 0) {
      const prevIndex = currentPanelIndex - 1;
      setCurrentPanelIndex(prevIndex);
      setSelectedPanel(formPanels[prevIndex]);
    }
  };

  // Sync selectedPanel when currentPanelIndex changes from store
  useEffect(() => {
    if (formPanels.length > 0 && formPanels[currentPanelIndex]) {
      setSelectedPanel(formPanels[currentPanelIndex]);
    }
  }, [currentPanelIndex, formPanels]);

  const isFirstPanel = currentPanelIndex === 0;

  // Confirmation panel: the panel with is_confirmation_panel: true (shown after submit)
  const confirmationPanelIndex = formPanels.findIndex(p => p.is_confirmation_panel);
  const hasConfirmationPanel = confirmationPanelIndex !== -1;
  const isConfirmationPanel = !!formPanels[currentPanelIndex]?.is_confirmation_panel;

  // Last submittable panel = last panel before the confirmation panel (or last panel if none)
  const lastSubmittableIndex = hasConfirmationPanel
    ? confirmationPanelIndex - 1
    : formPanels.length - 1;
  const isLastPanel = currentPanelIndex === lastSubmittableIndex;

  // Progress bar: exclude the confirmation panel from count and current index
  const submittablePanelCount = hasConfirmationPanel ? confirmationPanelIndex : formPanels.length;
  const progressIndex = isConfirmationPanel ? submittablePanelCount - 1 : currentPanelIndex;

  useEffect(() => {
    if (selectedPanel?.id) {
      dispatch(getFormPanelWidgets({ api_key, panel_id: selectedPanel.id }))
        .then(w => {
          // Sort widgets by priority, then group each widget's components by their
          // internal row — this prevents components from different widgets sharing
          // a grid row just because they have the same row number.
          const sortedWidgets = [...(w || [])].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

          const rowsArray = sortedWidgets.flatMap(widget => {
            const widgetComponents = (widget.components || []).filter(Boolean);

            // Group by row within the widget
            const byRow = widgetComponents.reduce((acc, c) => {
              const row = c.row ?? 0;
              if (!acc[row]) acc[row] = [];
              if (!acc[row].find(existing => existing.id === c.id)) {
                acc[row].push(c);
              }
              return acc;
            }, {});

            // Return one array per row, sorted by column then priority
            return Object.keys(byRow)
              .sort((a, b) => Number(a) - Number(b))
              .map(row =>
                byRow[row].sort((a, b) => {
                  const colA = a.column ?? 0;
                  const colB = b.column ?? 0;
                  if (colA !== colB) return colA - colB;
                  return (a.priority ?? 0) - (b.priority ?? 0);
                })
              )
              .filter(row => row.length > 0);
          });

          setComponensts(rowsArray);

          // Accumulate all components seen across panels (for cross-panel required field validation).
          // Dedup by lexicon (not id) so that if the form designer changes a component's id the
          // old stale entry is replaced rather than kept alongside the new one.
          const flatComponents = rowsArray.flat();
          allComponentsRef.current = [
            ...allComponentsRef.current.filter(c => !flatComponents.find(fc => fc.lexicon && fc.lexicon === c.lexicon)),
            ...flatComponents,
          ];
        })
        .catch(error => {
          console.error('Error fetching widgets:', error);
        });
    }
  }, [selectedPanel, api_key]);

  const fetchFormConfigs = form_id => {
    if (!api_key || !form_id) {
      return;
    }
    dispatch(getFormConfigs({ api_key, form_id: form_id }))
      .then(data => {
        setFormConfigs(data);
      })
      .catch(error => {
        console.error('Error fetching form configs:', error);
        setFormConfigs([]);
        dispatch(addToast({ error: true, text: 'Failed to fetch form configs', id: nanoid() }));
      });
  };

  // Fetch session data to pre-fill form
  const fetchSessionData = useCallback(
    (session_id, api_key) => {
      if (!session_id || !api_key) {
        return;
      }
      // Always start with a clean slate so stale values from a previous session
      // never bleed into a new / empty session.
      resetForm();
      const promise = dispatch(getSession({ session_id, api_key }));
      promise
        .then(sessionData => {
          // Pre-fill form values from session (including signatures)
          if (sessionData?.values) {
            // Store original values to track changes
            setOriginalSessionValues(sessionData.values);
            // Pre-fill form with session values
            Object.keys(sessionData.values).forEach(key => {
              let value = sessionData.values[key];
              // Parse phone numbers from string format (e.g., "+447566224395") to object format
              if (typeof value === 'string' && value.startsWith('+')) {
                // Check if this is likely a phone field by checking component type
                // We'll parse it and let PhoneElement handle the format
                const phoneMatch = value.match(/^(\+\d{1,3})(.+)$/);
                if (phoneMatch) {
                  const [, countryCode, phoneNumber] = phoneMatch;
                  value = {
                    country_code: countryCode,
                    value: phoneNumber,
                  };
                }
              }
              setValue(key, value);
            });
          }
          // Set panel index if session has progress
          if (sessionData?.current_panel_index !== undefined) {
            setCurrentPanelIndex(sessionData.current_panel_index);
          }
        })
        .catch(error => {
          console.error('Error fetching session data:', error);
          // Don't show error toast - session might not exist yet (new form)
        });
    },
    [dispatch, setValue, setCurrentPanelIndex, resetForm]
  );

  useEffect(() => {
    if (form_id) {
      fetchFormDetails(form_id);
    }
  }, [form_id]);

  useEffect(() => {

    // Get sessionId from URL if not in prop
    const urlSessionId = getSessionIdFromURL();
    const currentSessionId = sessionIdProp || urlSessionId || sessionId;

    if (api_key && form_id) {
      fetchFormPanels(form_id);
      fetchFormConfigs(form_id);

      if (currentSessionId && api_key) {

        // First check if session is closed by fetching session details
        dispatch(getSessionDetails({ session_id: currentSessionId, api_key }))
          .then(sessionDetails => {
            // Check if session is closed (has session_end timestamp)
            const closed = !!(sessionDetails?.session_end || sessionDetails?.sessionEnd);
            setIsSessionClosed(closed);
          })
          .catch(error => {
            console.error('Error fetching session details:', error);
            // If we can't fetch details, assume session is open (don't lock)
            setIsSessionClosed(false);
          });

        // Then fetch session submission data
        dispatch(getSession({ session_id: currentSessionId, api_key }))
          .then(sessionData => {
            if (sessionData?.values) {
              // Store original values to track changes
              setOriginalSessionValues(sessionData.values);

              // Pre-fill form with session values
              Object.keys(sessionData.values).forEach(key => {
                let value = sessionData.values[key];

                // Parse phone numbers from string format (e.g., "+447566224395") to object format
                if (typeof value === 'string' && value.startsWith('+')) {
                  const phoneMatch = value.match(/^(\+\d{1,3})(.+)$/);
                  if (phoneMatch) {
                    const [, countryCode, phoneNumber] = phoneMatch;
                    value = {
                      country_code: countryCode,
                      value: phoneNumber,
                    };
                  }
                }

                // For CHOOSER/CHOOSERMULTI: Session data contains IDs (not names)
                // We pass the ID directly - components will find the choice by ID in their useEffect hooks
                // ChooserElement: finds choice by `c.id === value || c.name === value`
                // MultiChooseElement: checks `value?.includes(c.id)` for arrays
                // No conversion needed - IDs are passed as-is

                setValue(key, value);
              });
            }
          })
          .catch(error => {
            console.error('❌❌❌ Session fetch error:', error);
          });
      }
    }
  }, [api_key, form_id, sessionIdProp, dispatch, setValue]);

  // Fetch session data when both sessionId and api_key are available
  useEffect(() => {
    if (sessionId && api_key) {
      fetchSessionData(sessionId, api_key);
    }
  }, [sessionId, api_key, fetchSessionData]);

  const getConfigIdByType = configType => {
    const configData = formConfigs.find(config => config.tag === configType);
    const { config } = configData || {};
    if (!config) {
      return null;
    }
    return { id: config.id };
  };

  // Convert signature to Base64 if it's a data URL
  const convertSignatureToBase64 = signatureValue => {
    if (!signatureValue) return null;

    // If it's already a data URL (data:image/...), extract the base64 part
    if (signatureValue.startsWith('data:image/')) {
      // Remove the data URL prefix (e.g., "data:image/png;base64,")
      const base64Match = signatureValue.match(/^data:image\/[^;]+;base64,(.+)$/);
      if (base64Match && base64Match[1]) {
        return base64Match[1];
      }
      return signatureValue;
    }

    // If it's already base64 (no data URL prefix), return as is
    return signatureValue;
  };

  const onAllowUpdate = async () => {
    if (!sessionId || !api_key) {
      console.error('Cannot reopen session: missing sessionId or api_key');
      dispatch(addToast({ error: true, text: 'Cannot reopen session. Missing session information.', id: nanoid() }));
      return;
    }

    setLoading(true);
    try {
      await dispatch(reopenSession({ session_id: sessionId, api_key }));
      setIsSessionClosed(false);
      dispatch(addToast({ error: false, text: 'Session unlocked. You can now update the form.', id: nanoid() }));
    } catch (error) {
      console.error('Error reopening session:', error);
      dispatch(addToast({ error: true, text: 'Failed to unlock session. Please try again.', id: nanoid() }));
    } finally {
      setLoading(false);
    }
  };

  const onDone = async () => {
    if (isSessionClosed) {
      dispatch(addToast({ error: true, text: 'This session is closed and cannot be modified.', id: nanoid() }));
      return;
    }

    // Client-side required field validation across all visited panels
    const missingRequired = allComponentsRef.current.filter(c => {
      if (!c.is_required || !c.lexicon) return false;
      const val = values[c.lexicon];
      return val === null || val === undefined || val === '';
    });

    if (missingRequired.length > 0) {
      // Mark each missing field red
      const errors = {};
      missingRequired.forEach(c => {
        errors[c.lexicon] = true;
        const fieldName = (c.label || c.name || 'Unknown field').split('\n')[0].trim().slice(0, 60);
        dispatch(addToast({ error: true, title: 'Required field', text: fieldName, id: nanoid() }));
      });
      setFieldErrors(errors);
      return;
    }

    setLoading(true);

    try {
      if (sessionId && api_key) {
        // Re-fetch the current panel's widgets to get the freshest component IDs.
        // This prevents NOT_FOUND errors caused by stale IDs from earlier navigations
        // or form designer changes made since the last fetch.
        let freshComponents = components;
        try {
          const freshWidgets = await dispatch(getFormPanelWidgets({ api_key, panel_id: selectedPanel?.id }));
          if (freshWidgets && freshWidgets.length > 0) {
            const sortedWidgets = [...freshWidgets].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
            const rowsArray = sortedWidgets.flatMap(widget => {
              const widgetComponents = (widget.components || []).filter(Boolean);
              const byRow = widgetComponents.reduce((acc, c) => {
                const row = c.row ?? 0;
                if (!acc[row]) acc[row] = [];
                if (!acc[row].find(ex => ex.id === c.id)) acc[row].push(c);
                return acc;
              }, {});
              return Object.keys(byRow)
                .sort((a, b) => Number(a) - Number(b))
                .map(row => byRow[row].sort((a, b) => (a.column ?? 0) - (b.column ?? 0) || (a.priority ?? 0) - (b.priority ?? 0)))
                .filter(row => row.length > 0);
            });
            freshComponents = rowsArray;
            // Merge fresh IDs into allComponentsRef so validation uses latest data too
            const flatFresh = rowsArray.flat();
            allComponentsRef.current = [
              ...allComponentsRef.current.filter(c => !flatFresh.find(fc => fc.lexicon === c.lexicon)),
              ...flatFresh,
            ];
          }
        } catch (fetchErr) {
        }

        // Flush any pending per-field debounce saves before closing
        const pendingLexicons = Object.keys(fieldDebounceRef.current);
        if (pendingLexicons.length > 0) {
          await Promise.all(
            pendingLexicons.map(lexicon => {
              clearTimeout(fieldDebounceRef.current[lexicon]);
              delete fieldDebounceRef.current[lexicon];
              const value = values[lexicon];
              return dispatch(updateSession({ session_id: sessionId, api_key, values: { [lexicon]: value }, components: freshComponents }))
                .catch(err => {
                  // NOT_FOUND means the component ID is stale — field was already saved or
                  // the component no longer exists. Don't block submission.
                  const status = err?.response?.status ?? err?.status;
                  if (status === 404) {
                    return null;
                  }
                  throw err;
                });
            })
          );
        }

        await dispatch(closeSession({ session_id: sessionId, api_key }));
      } else {
      }

      if (hasConfirmationPanel) {
        setCurrentPanelIndex(confirmationPanelIndex);
        setSelectedPanel(formPanels[confirmationPanelIndex]);
      } else {
        setSuccess(true);
        resetState();
      }
    } catch (error) {
      console.error('Error submitting form:', error);

      // Parse API validation error — returns missing required component names
      const apiError = error?.response?.data;
      if (apiError?.error_code === 'BAD_REQUEST' && Array.isArray(apiError?.components)) {
        apiError.components.forEach(c => {
          const fieldName = (c.name || 'Unknown field').split('\n')[0].trim().slice(0, 60);
          dispatch(addToast({ error: true, title: 'Required field', text: fieldName, id: nanoid() }));
        });
      } else {
        dispatch(addToast({ error: true, text: 'Failed to submit form. Please try again.', id: nanoid() }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Per-field debounce map: { [lexicon]: timeoutId }
  const fieldDebounceRef = React.useRef({});

  // Accumulate all components across all panels as user navigates
  const allComponentsRef = React.useRef([]);

  const onChange = (lexicon, value) => {
    setValue(lexicon, value);

    // Clear inline error for this field as soon as the user provides a value
    if (value !== null && value !== undefined && value !== '') {
      setFieldErrors(prev => {
        if (!prev[lexicon]) return prev;
        const next = { ...prev };
        delete next[lexicon];
        return next;
      });
    }

    if (!sessionId || !api_key || isSessionClosed || isConfirmationPanel) return;

    // Capture current panel components at call time — not via ref at fire time,
    // so navigating away before the debounce fires still uses the correct panel's IDs.
    const capturedComponents = components;

    // Clear any pending save for this specific field
    if (fieldDebounceRef.current[lexicon]) {
      clearTimeout(fieldDebounceRef.current[lexicon]);
    }

    // Schedule a save for just this field after 1s of inactivity
    fieldDebounceRef.current[lexicon] = setTimeout(() => {
      delete fieldDebounceRef.current[lexicon];

      const doSave = comps =>
        dispatch(updateSession({ session_id: sessionId, api_key, values: { [lexicon]: value }, components: comps }));

      doSave(capturedComponents)
        .then(() => {
          setOriginalSessionValues(prev => ({ ...prev, [lexicon]: value }));
        })
        .catch(err => {
          const status = err?.response?.status ?? err?.status;
          if (status === 404) {
            // Component ID is stale — re-fetch widgets and retry once with fresh IDs
            dispatch(getFormPanelWidgets({ api_key, panel_id: selectedPanel?.id }))
              .then(freshWidgets => {
                if (!freshWidgets) return;
                const sorted = [...freshWidgets].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
                const freshRows = sorted.flatMap(widget => {
                  const byRow = (widget.components || []).filter(Boolean).reduce((acc, c) => {
                    const row = c.row ?? 0;
                    if (!acc[row]) acc[row] = [];
                    if (!acc[row].find(ex => ex.id === c.id)) acc[row].push(c);
                    return acc;
                  }, {});
                  return Object.keys(byRow)
                    .sort((a, b) => Number(a) - Number(b))
                    .map(r => byRow[r].sort((a, b) => (a.column ?? 0) - (b.column ?? 0) || (a.priority ?? 0) - (b.priority ?? 0)))
                    .filter(r => r.length > 0);
                });
                // Update ref with fresh IDs keyed by lexicon
                const flatFresh = freshRows.flat();
                allComponentsRef.current = [
                  ...allComponentsRef.current.filter(c => !flatFresh.find(fc => fc.lexicon && fc.lexicon === c.lexicon)),
                  ...flatFresh,
                ];
                return doSave(freshRows);
              })
              .then(() => {
                setOriginalSessionValues(prev => ({ ...prev, [lexicon]: value }));
              })
              .catch(retryErr => {
                console.error('❌ Field save failed after retry:', lexicon, retryErr?.response?.data || retryErr);
              });
          } else {
            console.error('❌ Error saving field:', lexicon, err);
          }
        });
    }, 1000);
  };
  return (
    <MainWrapper
      className="w-full main flex-column items-center flex-1"
      transparency={transparency}
      style={{ backgroundColor: panel_color }}
    >
      {success ? (
        <div
          className="flex-column items-center justify-center flex-1"
          style={{
            maxWidth: maxPanelWidth ? `${maxPanelWidth}px` : formDetails?.attributes?.[0]?.panel_width ? `${formDetails.attributes[0].panel_width}px` : '100%',
            width: '100%',
          }}
        >
          <label className="inter-700-text natural-900-text font-26">
            {formDetails?.attributes?.[0]?.success_title || 'Thank you'}
          </label>
          <label className="inter-500-text natural-900-text font-20 mt-4 text-center" style={{ whiteSpace: 'pre-line' }}>
            {formDetails?.attributes?.[0]?.success_body ||
              'Thank you for taking the time to complete our resident feedback form.\n\nYour feedback is greatly appreciated and plays an important role in helping us maintain and continually monitor the quality of our workmanship, service, and resident experience.'}
          </label>
          {sessionId && (
            <div
              style={{
                marginTop: '20px',
                padding: '12px 20px',
                backgroundColor: '#e3f2fd',
                border: '2px solid #2196f3',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#1976d2',
              }}
            >
              <div style={{ marginBottom: '4px' }}>Session ID:</div>
              <div style={{ fontFamily: 'monospace', fontSize: '16px' }}>{sessionId}</div>
            </div>
          )}
        </div>
      ) : (
        <div
          className="flex-column main-panel w-full"
          style={{
            maxWidth: maxPanelWidth ? `${maxPanelWidth}px` : formDetails?.attributes?.[0]?.panel_width ? `${formDetails.attributes[0].panel_width}px` : '100%',
            width: '100%',
          }}
        >
          {isSessionClosed && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                marginBottom: '12px',
                backgroundColor: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#7a5c00',
                fontWeight: 500,
              }}
            >
              <LockIcon width={16} height={16} color="#7a5c00" />
              <span>This response has been submitted. Click <strong>Allow Update</strong> to make changes.</span>
            </div>
          )}
          <OrganisationContext.Provider value={{ api_key, getConfigIdByType }}>
            <div className="wrapper flex-1">
              {components.map((r, i) => {
                // Calculate max column number in this row to set proper grid columns
                const maxColumn = Math.max(...r.map(c => c.column ?? 0), 0);
                const gridColumns = maxColumn > 0 ? maxColumn : r.length;
                // Check if this row contains MULTICHOICE components
                const hasMultichoice = r.some(c => c.component_type === 'MULTICHOICE');
                return (
                  <GridWrapper
                    key={i}
                    className="grid widget-grid mb-4"
                    columns={gridColumns}
                    hasMultichoice={hasMultichoice}
                  >
                    {r.map(c => {
                      // For MULTICHOICE components, use id as fallback if lexicon is null
                      const valueKey =
                        c.component_type === 'MULTICHOICE' && !c.lexicon ? `multichoice_${c.id}` : c.lexicon;
                      // Set grid-column based on component's column property (1-indexed)
                      const columnNumber = c.column ?? 0;
                      const gridColumnStyle = columnNumber > 0 ? { gridColumn: columnNumber } : {};
                      return (
                        <div
                          key={c.id}
                          style={{
                            minWidth: 0,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            ...gridColumnStyle,
                          }}
                        >
                          <WidgetComponent
                            component={c}
                            onChange={onChange}
                            value={values[valueKey] ?? ''}
                            attributes={formDetails?.attributes?.[0]}
                            disabled={isSessionClosed}
                            hasError={!!fieldErrors[valueKey]}
                          />
                        </div>
                      );
                    })}
                  </GridWrapper>
                );
              })}
            </div>
          </OrganisationContext.Provider>
          {components?.length > 0 && !isConfirmationPanel && (
            <div className="flex col-gap-3" style={{ width: '100%' }}>
              {/* Allow Update button - only show when session is closed */}
              {isSessionClosed && (
                <Button
                  disabled={false}
                  size="customsize"
                  loading={loading}
                  label="Allow Update"
                  icon={<UnlockIcon width={18} height={18} color="#ffffff" style={{ marginRight: '6px' }} />}
                  afterIcon={null}
                  className="flex primary no-select flex-1 cursor"
                  color="#ffffff"
                  style={{
                    backgroundColor: formDetails?.attributes?.[0]?.color?.button || '#2196f3',
                    borderColor: formDetails?.attributes?.[0]?.color?.button || '#2196f3',
                  }}
                  font={{ name: 'Lato', style: 'MEDIUM', size: '14px', color: '#ffffff' }}
                  onClick={onAllowUpdate}
                />
              )}
              {/* Previous button - only show if not first panel and session is not closed (or if closed, show after Allow Update) */}
              {!isFirstPanel && (
                <Button
                  disabled={isSessionClosed}
                  size="customsize"
                  loading={false}
                  label="Previous"
                  icon={null}
                  afterIcon={null}
                  className="flex secondary no-select flex-1 cursor"
                  color="#666"
                  style={{
                    backgroundColor: '#f5f5f5',
                    borderColor: '#ddd',
                    border: '1px solid #ddd',
                  }}
                  font={{ name: 'Lato', style: 'MEDIUM', size: '14px', color: '#666' }}
                  onClick={goToPreviousPanel}
                />
              )}
              {/* Next/Submit button */}
              {!isLastPanel ? (
                <Button
                  disabled={isSessionClosed}
                  size="customsize"
                  loading={false}
                  label="Next"
                  icon={null}
                  afterIcon={null}
                  className="flex primary no-select flex-1 cursor"
                  color="#ffffff"
                  style={{
                    backgroundColor: formDetails?.attributes?.[0]?.color?.button,
                    borderColor: formDetails?.attributes?.[0]?.color?.button,
                    marginLeft: isFirstPanel && !isSessionClosed ? '0' : '0',
                  }}
                  font={{ name: 'Lato', style: 'MEDIUM', size: '14px', color: '#ffffff' }}
                  onClick={goToNextPanel}
                />
              ) : (
                <Button
                  disabled={isSessionClosed}
                  size="customsize"
                  loading={loading}
                  label="Submit"
                  icon={null}
                  afterIcon={null}
                  className="flex primary no-select flex-1 cursor"
                  color="#ffffff"
                  style={{
                    backgroundColor: formDetails?.attributes?.[0]?.color?.button,
                    borderColor: formDetails?.attributes?.[0]?.color?.button,
                    marginLeft: isFirstPanel && !isSessionClosed ? '0' : '0',
                  }}
                  font={{ name: 'Lato', style: 'MEDIUM', size: '14px', color: '#ffffff' }}
                  onClick={onDone}
                />
              )}
            </div>
          )}
          {formDetails?.attributes?.[0]?.progress_display && !isConfirmationPanel && components?.length > 0 && (
            <ProgressBar
              currentIndex={progressIndex}
              totalPanels={submittablePanelCount}
              position={formDetails?.attributes?.[0]?.progress_display}
              buttonColor={formDetails?.attributes?.[0]?.color?.button}
            />
          )}
        </div>
      )}
    </MainWrapper>
  );
};

const GridWrapper = styled('div')`
  display: grid;
  grid-template-columns: ${({ columns }) => `repeat(${columns}, 1fr)`};
  grid-column-gap: ${({ hasMultichoice }) => (hasMultichoice ? '24px' : '12px')};
  grid-row-gap: 16px;
  width: 100%;
  @media only screen and (max-width: 600px) {
    grid-template-columns: 1fr;
    grid-column-gap: 0;
  }
`;

export default Main;
